// The journey as the Map's stop bar walks it (WI-04): one stop per stage, in order, repeats
// included, the Map HUD's one fact (the top pill), and the dock's cues (today's plan, the Learn
// goal) that replaced the action card under the pill. Pure, unit-tested; three-free so the HUD
// (main bundle) can use it.
import type { Stage } from '../../shared/pack/schema'
import { MYSTERY_SPOT } from '../../shared/tripVisible'
import type { AgendaItem } from '../domain/agenda'
import type { TripClock } from '../domain/tripClock'
import { fmt, t } from '../strings/en'
import { PLACES, placeId } from './places/registry'

export interface JourneyStop {
  stageId: string
  /** The stage's own name ("Niseko, again"), or the wrapped surprise's caption. */
  name: string
  /** "22–26 Jan"; null for a wrapped surprise (its dates would give it away). */
  dates: string | null
  /** The island it stands on (today's place id), or null when it has none to show (wrapped). */
  spot: string | null
  /**
   * That island's display name ("Niseko"): the top pill's short name for the stop. Null with no
   * island of its own (wrapped, or a stand-in island for a stage with none yet).
   */
  place: string | null
  /** A surprise stage that hasn't opened for this viewer (shared/tripVisible). */
  wrapped: boolean
}

/**
 * The trip's stops: its stages in order, a repeat visit its own stop on the same island. Stages
 * come as the viewer may see them (a child's wrapped surprise is a `mystery` stage) and as the Map
 * shows them (map/bindings mapView). `own` are the same stages as the trip names them, before the
 * Map stood an island in for a stage with none (mapView): such a stop keeps its island but not the
 * island's name, so the pill never calls a Paris stay "Castle Town".
 */
export function journeyStops(stages: readonly Stage[], own: readonly Stage[] = stages): JourneyStop[] {
  return stages.map((s, i) => {
    const wrapped = s.spot === MYSTERY_SPOT
    const id = placeId(s.spot)
    const place = wrapped ? undefined : PLACES.find((p) => p.id === id)
    const standIn = own[i] !== undefined && placeId(own[i].spot) !== id
    return {
      stageId: s.id,
      name: wrapped ? t.map.stops.surprise : s.name,
      dates: wrapped ? null : fmt.shortRange(s.startDate, s.endDate),
      spot: place ? id : null,
      place: place && !standIn ? place.title : null,
      wrapped,
    }
  })
}

/** The stop the family is at: the first before the trip, the stage now during it, the last after. */
export function nowStop(clock: TripClock, count: number): number {
  if (!count) return 0
  if (clock.phase === 'pre') return 0
  if (clock.phase === 'after') return count - 1
  return Math.min(clock.stageIndex, count - 1)
}

/**
 * The stop to show for an island: the one we're at when it's on that island, else its first visit,
 * else null (an Explore-all island off the trip).
 */
export function stopFor(stops: readonly JourneyStop[], island: string, now: number): number | null {
  if (stops[now]?.spot === island) return now
  const i = stops.findIndex((s) => s.spot === island)
  return i < 0 ? null : i
}

/**
 * The top pill's one fact: the countdown to the next stop before the trip, the day and stop during
 * it. It names the stop by its place ("Day 19 · Niseko"), short enough for the pill; the stop bar
 * carries the stage's own name. A stop with no place name falls back to the stage's.
 */
export function pillText(clock: TripClock, stops: readonly JourneyStop[]): string {
  if (clock.phase === 'after') return t.map.pill.after
  const stop = stops[nowStop(clock, stops.length)]
  const name = stop?.place ?? stop?.name ?? ''
  if (clock.phase === 'pre') return stop?.wrapped ? t.map.pill.preSurprise(clock.daysToGo) : t.map.pill.pre(clock.daysToGo, name)
  return t.map.pill.during(clock.dayNumber, name)
}

/** A plan that started this recently is still today's next thing. */
export const TODAY_GRACE_MS = 15 * 60 * 1000

/**
 * Today's next plan during the trip (one that started in the last TODAY_GRACE_MS still counts),
 * else null. The dock's Trip tab shows a dot while there is one (owner, 23 Oct: the Map's
 * one-line action card took up too much of the island; its cues moved to the dock's badges).
 */
export function todayNext(clock: TripClock, nowMs: number, agenda: readonly AgendaItem[]): AgendaItem | null {
  if (clock.phase !== 'during') return null
  return agenda.find((i) => i.date === clock.today && i.startMs >= nowMs - TODAY_GRACE_MS) ?? null
}

/**
 * The dock's Learn badge: right answers still needed for today's goal (and its play), or null
 * when the goal's done, or there's no goal (no Learn tab, or someone who doesn't play).
 */
export function learnBadge(left: number | null): number | null {
  return left !== null && left > 0 ? left : null
}
